/**
 * ACT2-1108: "Running in the background" in the setup panel — the setup work that keeps going after the
 * admin moves on, one row per job, gone once the job is done:
 *
 * - mapping a connected warehouse (the background Warehouse orientation run, ACT2-1109), read from
 *   `settings.warehouses.listAccounts`;
 * - writing a warehouse account's documentation from its linked repos, read from
 *   `warehouseProjects.list({ accountKey })` — the same status the repo step's linker shows as "Generating…".
 *
 * A failed job stays as a row with its short reason: neither job has a retry here, so the row is how the
 * admin learns it did not finish. (A map that did not finish can be run again from the connection's
 * menu in Settings › Warehouses, ACT2-1340.)
 */
import type { WarehouseOrientationReason } from '@server/types';
import { useQueries, useQuery } from '@tanstack/react-query';
import { CircleAlert, LoaderCircle } from 'lucide-react';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { cn } from '@/_shared/lib/utils';
import { nameWarehouseAccounts } from '@/settings/components/warehouseAccount/useWarehouseAccounts';

/** How often a running job's status is re-read. */
const BACKGROUND_POLL_MS = 5_000;

interface BackgroundJob {
  id: string;
  /** What is running, e.g. "Mapping the warehouse". */
  what: string;
  /** Which account or repo it is for. */
  subject: string;
  state: 'running' | 'failed';
  /** Why a failed job failed, in a few words. */
  reason?: string;
}

/** Long backend errors are cut to a line; the full text is in the row's tooltip. */
const REASON_MAX = 80;

function shortReason(reason: string | null | undefined): string | undefined {
  const text = reason?.trim();
  if (!text) return undefined;
  return text.length > REASON_MAX ? `${text.slice(0, REASON_MAX - 1)}…` : text;
}

/** Why a map was blocked, in a few words (ACT2-1340). A paused connection is not a sign-in failure. */
function blockedReason(reason: WarehouseOrientationReason | null): string {
  switch (reason) {
    case 'credential':
      return 'Can’t sign in';
    case 'credential_unavailable':
      return 'No credentials';
    case 'paused':
      return 'Connection paused';
    case 'no_reads':
    case 'partial':
    case null:
      return 'Couldn’t read';
  }
}

/** The setup jobs in flight or failed, for the panel. Empty while nothing is running. */
function useBackgroundJobs(): BackgroundJob[] {
  const orpc = useOrpc();
  const { data } = useQuery({
    ...orpc.settings.warehouses.listAccounts.queryOptions(),
    refetchInterval: (query) =>
      (query.state.data?.accounts ?? []).some(
        (account) => account.orientation?.status === 'queued' || account.orientation?.status === 'running',
      )
        ? BACKGROUND_POLL_MS
        : false,
  });
  const accounts = nameWarehouseAccounts(data?.accounts ?? []);

  // One documentation job per warehouse account with a linked repo. Several connections can share an
  // account, so the first one names it.
  const docAccounts = new Map<string, string>();
  for (const account of accounts) {
    if (account.warehouseProjectCount > 0 && !docAccounts.has(account.accountKey)) {
      docAccounts.set(account.accountKey, account.name);
    }
  }
  const docQueries = useQueries({
    queries: [...docAccounts.keys()].map((accountKey) => ({
      ...orpc.warehouseProjects.list.queryOptions({ input: { accountKey } }),
      refetchInterval: (query: { state: { data?: { doc: { status: string } | null } } }) =>
        query.state.data?.doc?.status === 'generating' ? BACKGROUND_POLL_MS : false,
    })),
  });

  const mapping = accounts.flatMap((account): BackgroundJob[] => {
    const base = { id: `map:${account.id}`, what: 'Mapping the warehouse', subject: account.name };
    switch (account.orientation?.status) {
      case 'queued':
      case 'running':
        return [{ ...base, state: 'running' }];
      // The retry lives in the connection's menu in Settings › Warehouses (ACT2-1340).
      case 'failed':
        return [{ ...base, state: 'failed' }];
      case 'blocked':
        return [{ ...base, state: 'failed', reason: blockedReason(account.orientation.reason) }];
      case 'interrupted':
        return [{ ...base, state: 'failed', reason: 'Interrupted' }];
      case 'done':
      case undefined:
        return [];
    }
  });

  const docs = [...docAccounts.entries()].flatMap(([accountKey, name], index): BackgroundJob[] => {
    const result = docQueries[index]?.data;
    const doc = result?.doc;
    const repos = (result?.projects ?? []).map((project) => project.repoFullName).join(', ');
    const base = { id: `docs:${accountKey}`, what: 'Writing docs from repos', subject: repos || name };
    switch (doc?.status) {
      case 'generating':
        return [{ ...base, state: 'running' }];
      case 'failed':
        return [{ ...base, state: 'failed', reason: shortReason(doc.error) }];
      case 'ready':
      case undefined:
        return [];
    }
  });

  return [...mapping, ...docs];
}

export function BackgroundJobs(): React.ReactElement | null {
  const jobs = useBackgroundJobs();
  if (jobs.length === 0) return null;
  return (
    <section
      aria-labelledby="setup-background-title"
      className="flex flex-col gap-1.5 border-t border-border px-3 pt-4"
    >
      <h3 id="setup-background-title" className="text-xs font-medium text-muted-foreground">
        Running in the background
      </h3>
      <div role="status">
        <ul aria-labelledby="setup-background-title" className="flex flex-col gap-1">
          {jobs.map((job) => {
            const state = job.state === 'running' ? 'Running' : job.reason ? `Failed · ${job.reason}` : 'Failed';
            return (
              <li key={job.id} className="flex items-center gap-2 text-xs" title={`${job.what} · ${job.subject}`}>
                {job.state === 'running' ? (
                  <LoaderCircle aria-hidden className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
                ) : (
                  <CircleAlert aria-hidden className="size-3.5 shrink-0 text-danger" />
                )}
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-foreground">{job.what}</span>
                  <span className="truncate text-muted-foreground">{job.subject}</span>
                </span>
                <span
                  className={cn(
                    'max-w-[45%] shrink-0 truncate text-right',
                    job.state === 'failed' ? 'text-danger' : 'text-muted-foreground',
                  )}
                  title={state}
                >
                  {state}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
